import type { AgeRules } from '$saga/views/Tour/types';

export type AgeInputVisibilityParams = {
    isQuote: boolean;
    hasTravelGateHotels: boolean;
    hasMultipleAgePricing: boolean;
};

export function shouldShowIndividualAges({
    isQuote,
    hasTravelGateHotels,
    hasMultipleAgePricing,
}: AgeInputVisibilityParams): boolean {
    return !isQuote && (hasTravelGateHotels || hasMultipleAgePricing);
}

export type PaxLimitsParams = {
    isEbookingType: boolean;
    isGir: boolean;
    isChildrenAllowed: boolean;
    isTravelAloneAuthorized: boolean;
    ageRules: AgeRules;
    adults: number;
    children: number;
    selectedDate: string | null;
    remainingStock: number | null;
};

export type PaxLimits = {
    minAdults: number;
    maxAdults: number | null;
    minChildren: number;
    maxChildren: number | null;
};

export type PaxValidityParams = {
    adults: number;
    children: number;
    minAdults: number;
    maxAdults: number | null;
    minChildren: number;
    maxChildren: number | null;
    minTotalPax: number;
    requiredPax?: number | null;
};

/**
 * Computes the minimum total pax required for a booking.
 * Solo travel is allowed only when isTravelAloneAuthorized.
 */
export function computeMinTotalPax(isTravelAloneAuthorized: boolean): number {
    return isTravelAloneAuthorized ? 1 : 2;
}

/**
 * Computes whether children are allowed on this tour.
 * Children are only allowed on ebooking tours where the age range includes under-18s.
 */
export function computeIsChildrenAllowed(
    isEbookingType: boolean,
    minAgeAllowed: number | null,
    maxAgeAllowed: number | null,
): boolean {
    return (
        isEbookingType &&
        ((minAgeAllowed ?? 0) < 18 || (!!maxAgeAllowed && maxAgeAllowed < 18))
    );
}

/**
 * Derives min/max rule values for the adults and children inputs.
 *
 * - minAdults: 0 when the tour is under-18 only, otherwise 1.
 * - maxAdults: capped by remaining stock minus current children count (GIR + ebooking + date selected).
 * - minChildren: 0 (children are always optional).
 * - maxChildren: capped by remaining stock minus current adults count (GIR only).
 */
export function computePaxLimits({
    isEbookingType,
    isGir,
    isChildrenAllowed,
    ageRules,
    adults,
    children,
    selectedDate,
    remainingStock,
}: PaxLimitsParams): PaxLimits {
    const minAdults = ageRules.max.isUnder18Allowed ? 0 : 1;

    const maxAdults =
        isEbookingType &&
        isGir &&
        selectedDate !== null &&
        remainingStock !== null
            ? remainingStock - children
            : null;

    const minChildren = 0;

    const maxChildren =
        isGir && isChildrenAllowed && remainingStock !== null
            ? remainingStock - adults
            : null;

    return { minAdults, maxAdults, minChildren, maxChildren };
}

/**
 * Determines whether the current pax selection is valid for form submission.
 */
export function computeIsPaxValid({
    adults,
    children,
    minAdults,
    maxAdults,
    minChildren,
    maxChildren,
    minTotalPax,
    requiredPax = null,
}: PaxValidityParams): boolean {
    return (
        adults >= minAdults &&
        children >= minChildren &&
        (maxAdults === null || adults <= maxAdults) &&
        (maxChildren === null || children <= maxChildren) &&
        adults + children >= minTotalPax &&
        (requiredPax === null || adults + children === requiredPax)
    );
}

export function resolveAdultMaxAge(
    adultMaxAge: number | null,
    displayMaxAgeAllowed: number | null,
    validationMaxAgeAllowed: number | null = displayMaxAgeAllowed,
): number | null {
    return adultMaxAge ?? validationMaxAgeAllowed;
}

export const ROOM_ADULT_HARD_MAX = 10;

export function getEffectiveRoomAdultMax(
    maxAdultsRuleValue: number | null,
): number {
    return Math.min(
        maxAdultsRuleValue ?? ROOM_ADULT_HARD_MAX,
        ROOM_ADULT_HARD_MAX,
    );
}

export function normalizePaxCount(
    value: string | number,
    minimum: number,
    maximum: number | null,
): number {
    const parsedValue = Number(value);
    const count = Number.isInteger(parsedValue) ? parsedValue : minimum;

    return Math.max(
        minimum,
        maximum === null ? count : Math.min(count, maximum),
    );
}

export function alignAgesWithCount(ages: string[], count: number): string[] {
    const alignedAges = ages.slice(0, count);

    return [
        ...alignedAges,
        ...Array.from({ length: count - alignedAges.length }, () => ''),
    ];
}

export function areAgesValid(
    ages: string[],
    count: number,
    minAge: number,
    maxAge: number | null,
): boolean {
    return Array.from({ length: count }, (_, index) => ages[index] ?? '').every(
        (age) => {
            const value = Number(age);

            return (
                age !== '' &&
                Number.isInteger(value) &&
                value >= minAge &&
                (maxAge === null || value <= maxAge)
            );
        },
    );
}

export function hasAgeOutsideBounds(
    ages: string[],
    count: number,
    minAge: number,
    maxAge: number | null,
): boolean {
    return Array.from({ length: count }, (_, index) => ages[index] ?? '').some(
        (age) => age !== '' && !areAgesValid([age], 1, minAge, maxAge),
    );
}

export function hasAgeBelowMinimum(
    ages: string[],
    count: number,
    minAge: number,
): boolean {
    return Array.from({ length: count }, (_, index) => ages[index] ?? '').some(
        (age) => age !== '' && Number(age) < minAge,
    );
}
